import {inject, injectable} from 'inversify';
import {toJS} from 'mobx';

import type {BNUserProfileUpdateParams} from '../../../../../api/types/bnrks/methods';
import type {BNMembershipApiModel} from '../../../../../api/types/bnrks/models';
import type {CoreUserProfileUpdateParams} from '../../../../../api/types/core/methods';
import type {CoreMembershipApiModel} from '../../../../../api/types/core/models';
import type {Combine} from '../../../../../utils/utility-types';
import type {PrivateProfileModel} from '../PrivateProfile.model';

import {valueOrNull} from '../../../../../utils/valueOrNull';
import {EnvProviderService} from '../../../../system/env/EnvProvider.service';
import {DateTimeModelFactory} from '../../../Date/DateTime.factory';
import {UserAdditionalDataMapperJrpc} from '../../UserAdditionalData/mappers/UserAdditionalData.mapper.jrpc';
import {PrivateProfileFactory} from '../PrivateProfile.factory';

@injectable()
export class PrivateProfileMapperJrpc {
  constructor(
    @inject(PrivateProfileFactory)
    private readonly factory: PrivateProfileFactory,
    @inject(DateTimeModelFactory)
    private readonly dateTimeFactory: DateTimeModelFactory,
    @inject(UserAdditionalDataMapperJrpc)
    private readonly additionalDataMapper: UserAdditionalDataMapperJrpc,
    @inject(EnvProviderService) private readonly env: EnvProviderService,
  ) {}

  mapDataToModel(
    apiData: PrivateProfileMapperJrpc.ApiData,
  ): PrivateProfileModel {
    return this.factory.create(this.mapDataToProps(apiData));
  }

  mapDataToProps(
    apiData: PrivateProfileMapperJrpc.ApiData,
  ): PrivateProfileModel.Props {
    const props = {
      additionalData: this.additionalData(apiData),
      dateOfBirth: this.dateOfBirth(apiData),
      email: this.email(apiData),
      firstName: this.firstName(apiData),
      gender: this.gender(apiData),
      lastName: this.lastName(apiData),
      location: this.location(apiData),
      middleName: this.middleName(apiData),
      partnerId: this.partnerId(apiData),
      phone: this.phone(apiData),
      reasonsForCoaching: this.reasonsForCoaching(apiData),
      timezone: this.timezone(apiData),
    };

    const hasAnyData = Object.values(props).some(value => value !== null);

    return {
      ...props,
      isEmpty: !hasAnyData,
    };
  }

  mapModelToDto(model: PrivateProfileModel): PrivateProfileMapperJrpc.Dto {
    const date_of_birth = valueOrNull(model.dateOfBirth?.toDateObj());
    const data = toJS(model);

    let dto: PrivateProfileMapperJrpc.Dto = {
      date_of_birth,
      email: data.email,
      first_name: data.firstName,
      gender: data.gender,
      last_name: data.lastName,
      location: data.location,
      middle_name: data.middleName,
      phone: data.phone,
      timezone: data.timezone,
    };

    if (this.env.app.isB2b) {
      dto = {
        ...dto,
        additional_data: data.additionalData
          ? this.additionalDataMapper.mapModelToDto(data.additionalData)
          : null,
        partner_id: data.partnerId,
        reasons_for_coaching: data.reasonsForCoaching,
      };
    }

    return dto;
  }

  private additionalData(
    apiData: PrivateProfileMapperJrpc.ApiData,
  ): PrivateProfileModel.Props['additionalData'] {
    if (!apiData.additional_data) {
      return null;
    }

    return this.additionalDataMapper.mapDataToModel(apiData.additional_data);
  }

  private dateOfBirth(
    apiData: PrivateProfileMapperJrpc.ApiData,
  ): PrivateProfileModel.Props['dateOfBirth'] {
    if (!apiData.date_of_birth) {
      return null;
    }

    return this.dateTimeFactory.createFromDateObject(apiData.date_of_birth);
  }

  private email(
    apiData: PrivateProfileMapperJrpc.ApiData,
  ): PrivateProfileModel.Props['email'] {
    return valueOrNull(apiData.email);
  }

  private firstName(
    apiData: PrivateProfileMapperJrpc.ApiData,
  ): PrivateProfileModel.Props['firstName'] {
    return valueOrNull(apiData.first_name);
  }

  private gender(
    apiData: PrivateProfileMapperJrpc.ApiData,
  ): PrivateProfileModel.Props['gender'] {
    return valueOrNull(apiData.gender);
  }

  private lastName(
    apiData: PrivateProfileMapperJrpc.ApiData,
  ): PrivateProfileModel.Props['lastName'] {
    return valueOrNull(apiData.last_name);
  }

  private location(
    apiData: PrivateProfileMapperJrpc.ApiData,
  ): PrivateProfileModel.Props['location'] {
    return valueOrNull(apiData.location);
  }

  private middleName(
    apiData: PrivateProfileMapperJrpc.ApiData,
  ): PrivateProfileModel.Props['middleName'] {
    return valueOrNull(apiData.middle_name);
  }

  private partnerId(
    apiData: PrivateProfileMapperJrpc.ApiData,
  ): PrivateProfileModel.Props['partnerId'] {
    return valueOrNull(apiData.partner_id);
  }

  private phone(
    apiData: PrivateProfileMapperJrpc.ApiData,
  ): PrivateProfileModel.Props['phone'] {
    return valueOrNull(apiData.phone);
  }

  private reasonsForCoaching(
    apiData: PrivateProfileMapperJrpc.ApiData,
  ): PrivateProfileModel.Props['reasonsForCoaching'] {
    return valueOrNull(apiData.reasons_for_coaching);
  }

  private timezone(
    apiData: PrivateProfileMapperJrpc.ApiData,
  ): PrivateProfileModel.Props['timezone'] {
    return valueOrNull(apiData.timezone);
  }
}

export namespace PrivateProfileMapperJrpc {
  export type ApiData = Combine<
    CoreMembershipApiModel['private_profile'],
    BNMembershipApiModel['private_profile']
  >;

  export type Dto =
    | BNUserProfileUpdateParams['private_profile']
    | CoreUserProfileUpdateParams['private_profile'];
}
